import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";

import Hero from "src/components/Hero/Hero";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import EventFinished from "src/components/EventFinished";
import TitleSection from "src/components/TitleSection/TitleSection";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

const BigGameBash2026 = () => {
  const { t } = useTranslation();

  const bgImage = useResponsiveImageUrl(
    "entertainment/variety/big-game-bash-2026/superbowl-lx-prizes-background.png"
  );

  const eventPackageItems = t("bigGameBash2026.content.eventPackage.items", {
    returnObjects: true,
  });

  const eventDetailsItems = t("bigGameBash2026.content.eventDetails.items", {
    returnObjects: true,
  });

  return (
    <MainDiv bgImage={bgImage}>
      <Hero
        bgImage="entertainment/variety/big-game-bash-2026/superbowl-lx-hero-image-2400x800px.jpg"
        image={t("bigGameBash2026.hero.image")}
        imageAlt={t("bigGameBash2026.hero.title")}
      />
      <EventFinished
        scheduleKeyName="bigGameBash2026"
        eventType="entertainment"
      />
      <container-div>
        {/* Hero Title Section */}
        <TitleSection
          title={t("bigGameBash2026.content.header.title")}
          text={t("bigGameBash2026.content.header.text")}
          borderColor="#651B38"
          dark={false}
        />

        {/* Event Package Section - Red Box */}
        <EventPackageSection>
          <div className="package-images-left">
            <ResponsiveImage
              src="entertainment/variety/big-game-bash-2026/superbowl-lx-tickets-left-side.png"
              alt="Tickets"
            />
          </div>
          <div className="package-images-right">
            <ResponsiveImage
              src="entertainment/variety/big-game-bash-2026/superbowl-lx-tickets-right-side.png"
              alt="Tickets"
            />
          </div>
          <div className="package-content">
            <TitleSection
              as="h3"
              title={t("bigGameBash2026.content.eventPackage.title")}
              text={t("bigGameBash2026.content.eventPackage.presale")}
              borderColor="#651B38"
              dark={false}
            />
            <p className="includes-title">
              {t("bigGameBash2026.content.eventPackage.includes")}
            </p>
            <ul>
              {eventPackageItems.map((item, index) => (
                <li key={index}>
                  <ReactMarkdown>{item}</ReactMarkdown>
                </li>
              ))}
            </ul>
            <p className="disclaimer">
              {t("bigGameBash2026.content.eventPackage.disclaimer")}
            </p>
          </div>
        </EventPackageSection>

        {/* Games & Giveaways Section */}
        <GamesSection>
          <TitleSection
            title={t("bigGameBash2026.content.games.title")}
            text={t("bigGameBash2026.content.games.intro")}
            borderColor="#651B38"
            dark={false}
          />

          {/* Football Squares & Prop Bets - Two Columns */}
          <Row className="m-0 games-row">
            {/* Football Squares Column */}
            <Col xs={12} lg={6} className="game-column">
              <GameCard className="lightblack">
                <TitleWithIcon>
                  <IconWrapper>
                    <ResponsiveImage
                      src="entertainment/variety/big-game-bash-2026/superbowl-lx-football.png"
                      alt="Football"
                    />
                  </IconWrapper>
                  <h3>
                    {t("bigGameBash2026.content.games.footballSquares.title")}
                  </h3>
                </TitleWithIcon>
                <PrizeAmount>
                  {t("bigGameBash2026.content.games.footballSquares.amount")}
                </PrizeAmount>
                <p className="game-description">
                  {t(
                    "bigGameBash2026.content.games.footballSquares.description"
                  )}
                </p>
                <PrizesSection>
                  <h3>
                    {t(
                      "bigGameBash2026.content.games.footballSquares.prizesHeading"
                    )}
                  </h3>
                  <ReactMarkdown>
                    {t("bigGameBash2026.content.games.footballSquares.prizes")}
                  </ReactMarkdown>
                  <p className="disclaimer-italic">
                    {t(
                      "bigGameBash2026.content.games.footballSquares.disclaimer"
                    )}
                  </p>
                </PrizesSection>
                <LiveRafflesSection>
                  <h3>
                    {t(
                      "bigGameBash2026.content.games.footballSquares.liveRafflesHeading"
                    )}
                  </h3>
                  <p>
                    {t(
                      "bigGameBash2026.content.games.footballSquares.liveRaffles"
                    )}
                  </p>
                </LiveRafflesSection>
              </GameCard>
            </Col>

            {/* Prop Bets Column */}
            <Col xs={12} lg={6} className="game-column">
              <GameCard>
                <TitleWithIcon>
                  <IconWrapper>
                    <ResponsiveImage
                      src="entertainment/variety/big-game-bash-2026/superbowl-lx-football.png"
                      alt="Football"
                    />
                  </IconWrapper>
                  <h3>{t("bigGameBash2026.content.games.propBets.title")}</h3>
                </TitleWithIcon>
                <PrizeAmount>
                  {t("bigGameBash2026.content.games.propBets.amount")}
                </PrizeAmount>
                <p className="game-description">
                  {t("bigGameBash2026.content.games.propBets.description")}
                </p>
                <p className="oep">
                  {t("bigGameBash2026.content.games.propBets.oep")}
                </p>
                <PrizesSection>
                  <h4>
                    {t("bigGameBash2026.content.games.propBets.prizesHeading")}
                  </h4>
                  <ReactMarkdown>
                    {t("bigGameBash2026.content.games.propBets.prizes")}
                  </ReactMarkdown>
                </PrizesSection>

                {/* Grand Prize Bonus - Green Border Box - Inside Prop Bets */}
                <GrandPrizeBoxInline>
                  <h4 className="grand-prize-title">
                    {t("bigGameBash2026.content.games.grandPrize.title")}{" "}
                    <span className="bonus-text">
                      {t("bigGameBash2026.content.games.grandPrize.bonus")}
                    </span>
                  </h4>
                  <p>
                    <span className="green-text">
                      {t("bigGameBash2026.content.games.grandPrize.text1")}
                    </span>
                    <span className="white-italic">
                      {t("bigGameBash2026.content.games.grandPrize.text2")}
                    </span>
                    <span className="green-text">
                      {t("bigGameBash2026.content.games.grandPrize.text3")}
                    </span>
                    <span className="white-italic">
                      {t("bigGameBash2026.content.games.grandPrize.text4")}
                    </span>
                    <span className="green-text">
                      {t("bigGameBash2026.content.games.grandPrize.text5")}
                    </span>
                    <span className="white-italic">
                      {t("bigGameBash2026.content.games.grandPrize.text6")}
                    </span>
                  </p>
                </GrandPrizeBoxInline>
              </GameCard>
            </Col>
          </Row>
        </GamesSection>

        {/* BET99 Raffle Section */}
        <Bet99RaffleSection>
          <div className="raffle-container">
            <ReactMarkdown>
              {t("bigGameBash2026.content.games.bet99Raffle.title")}
            </ReactMarkdown>
            <div className="raffle-intro">
              <ReactMarkdown>
                {t("bigGameBash2026.content.games.bet99Raffle.description")}
              </ReactMarkdown>
            </div>
            <Row className="raffle-prizes">
              <Col xs={12} md={4} className="raffle-prize">
                <div className="prize-box">
                  {t("bigGameBash2026.content.games.bet99Raffle.prize1")}
                </div>
              </Col>
              <Col xs={12} md={4} className="raffle-prize">
                <div className="prize-box">
                  {t("bigGameBash2026.content.games.bet99Raffle.prize2")}
                </div>
              </Col>
              <Col xs={12} md={4} className="raffle-prize">
                <div className="prize-box">
                  {t("bigGameBash2026.content.games.bet99Raffle.prize3")}
                </div>
              </Col>
            </Row>
            <Bet99SignupBox>
              <ReactMarkdown>
                {t("bigGameBash2026.content.games.bet99Signup.text")}
              </ReactMarkdown>
            </Bet99SignupBox>
          </div>
          <p className="raffle-disclaimer">
            {t("bigGameBash2026.content.bottomText")}
          </p>
        </Bet99RaffleSection>

        {/* Watch Section */}
        <WatchSection>
          <TitleSection
            title={t("bigGameBash2026.content.watch.title")}
            text={t("bigGameBash2026.content.watch.text")}
            borderColor="#651B38"
            dark={false}
          />
        </WatchSection>

        {/* Event Details */}
        <EventDetailsSection>
          <Row className="m-0 align-items-center">
            <Col xs={12} md={6} className="event-image">
              <ResponsiveImage
                src={t("bigGameBash2026.content.eventDetails.image")}
                alt={t("bigGameBash2026.content.header.title")}
              />
            </Col>
            <Col xs={12} md={6} className="event-details-content">
              <h2>{t("bigGameBash2026.content.eventDetails.title")}</h2>
              <ul>
                {eventDetailsItems.map((item, index) => (
                  <li key={index}>
                    <ReactMarkdown>{item}</ReactMarkdown>
                  </li>
                ))}
              </ul>
            </Col>
          </Row>
        </EventDetailsSection>

        {/* Final Tagline */}
        <FinalTagline>
          <h2>{t("bigGameBash2026.content.finalTagline.title")}</h2>
          <p>{t("bigGameBash2026.content.finalTagline.subtitle")}</p>
        </FinalTagline>
      </container-div>
    </MainDiv>
  );
};

const MainDiv = styled.div`
  height: auto;
  width: 100%;
  background: var(--black);
  color: var(--white);
  position: relative;

  /* Background image positioned in vertical center */
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 100vh;
    transform: translateY(-50%);
    background-image: url(${(props) => props.bgImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 0;
    pointer-events: none;
  }
    
  .hero_text_container {
    display: none;
  }

  .hero_image_container {
    padding: 0;
    position: relative;
    z-index: 2;
  }

  container-div {
    position: relative;
    z-index: 1;
  }

  .disclaimer-text {
    font-size: 1.2rem;
    padding: 20px;
  }

  .title-section {
    p a,
    a {
      color: #4db84d !important;
      
      &:hover {
        color: #3da73d !important;
      }
    }
  }
`;

const EventPackageSection = styled.div`
  background: var(--mutedred);
  color: var(--white);
  padding: 40px 20px;
  position: relative;
  overflow: hidden;

  .title-section {
    padding: 0;
  }

  .package-images-left,
  .package-images-right {
    position: absolute;
    width: 240px;
    z-index: 1;

    img {
      width: 100%;
      height: auto;
    }
  }

  .package-images-left {
    left: 0;
    top: 0;
  }

  .package-images-right {
    right: 0;
    bottom: 0;
  }

  .package-content {
    position: relative;
    z-index: 2;
    text-align: center;
    max-width: 900px;
    margin: 0 auto;

    p {
      font-size: 1.2rem;
      line-height: 1.7;
      margin-bottom: 15px;

      &.includes-title {
        font-size: 28px;
        font-weight: 700;
        margin-top: 10px;
        margin-bottom: 20px;
      }

      &.disclaimer {
        font-size: 1.2rem;
        margin-top: 35px;
        font-style: italic;
      }

      strong {
        font-weight: 700;
      }
    }

    ul {
      list-style: disc;
      list-style-position: inside;
      padding: 0;
      margin: 15px 0 25px;
      max-width: 700px;
      margin-left: auto;
      margin-right: auto;

      li {
        text-align: center;
        font-size: 1.2rem;
        line-height: 1.7;
        margin-bottom: 10px;

        p {
          display: inline;
          margin: 0;
          font-size: 1.2rem;
          line-height: 1.7;

          strong {
            font-weight: 700;
          }

          a {
            color: var(--white);
            text-decoration: none;
            font-weight: 600;
            padding-bottom: 1px;
            border-bottom: 1px solid var(--white);
          }
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    .package-images-left,
    .package-images-right {
      width: 150px;
    }

    .package-images-left {
      left: 2%;
    }

    .package-images-right {
      right: 2%;
    }
  }

  @media screen and (max-width: 768px) {
    padding: 50px 20px;

    .package-images-left,
    .package-images-right {
      display: none;
    }

    .package-content {
      p {
        font-size: 1.2rem;
      }

      ul li {
        font-size: 1.2rem;
        padding-left: 25px;

        &:before {
          font-size: 24px;
        }

        p {
          font-size: 1.2rem;
        }
      }
    }
  }
`;

const GamesSection = styled.div`
  padding: 80px 20px;
  background: transparent;
  position: relative;

  .title-section {
    padding: 0 0 50px;
    text-align: center;
  }

  .games-row {
    display: flex;
    justify-content: center;
    gap: 40px;
    margin-bottom: 50px;
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
  }

  .game-column {
    padding: 0;
    flex: 1;
    max-width: 500px;
  }

  @media screen and (max-width: 991px) {
    padding: 60px 20px;

    .games-row {
      flex-direction: column;
      gap: 30px;
    }
  }
`;

const GameCard = styled.div`
  background: var(--lightblack);
  padding: 50px 40px;
  height: 100%;
  text-align: center;
  border-radius: 0;

  h3 {
    font-size: 26px;
    font-weight: 700;
    margin: 0;
    text-transform: capitalize;
  }

  .game-description {
    font-size: 1.2rem;
    line-height: 1.6;
    margin-bottom: 25px;
    color: var(--white);
  }

  .oep {
    font-size: 1.2rem;
    margin-bottom: 25px;
    color: var(--white);
    font-weight: 600;
  }
  @media screen and (max-width: 768px) {
    padding: 20px;
  }
`;

const TitleWithIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    width: 50px;
    height: 50px;
    object-fit: contain;
  }

  svg {
    width: 50px;
    height: 50px;
  }
`;

const PrizeAmount = styled.h4`
  font-weight: 700;
  color: var(--white);
  margin-bottom: 20px;
  line-height: 1.4;
  text-align: center;
`;

const PrizesSection = styled.div`
  margin-top: 30px;
  text-align: center;

  h4 {
    font-weight: 700;
    margin-bottom: 15px;
    text-transform: capitalize;
    text-align: center;
  }

  p {
    font-size: 1.2rem;
    line-height: 1.7;
    margin-bottom: 6px;
    color: var(--white);

    strong {
      font-weight: 700;
    }

    &.disclaimer-italic {
      font-style: italic;
      margin-top: 10px;
      font-size: 1.2rem;
    }
  }

  ul {
    list-style: disc;
    list-style-position: inside;
    margin: 15px 0;
    text-align: center;
    padding-left: 0;

    li {
      font-size: 1.2rem;
      margin-bottom: 8px;
      line-height: 1.6;
      color: #ddd;
      text-align: center;

      strong {
        font-weight: 700;
        color: var(--white);
      }
    }
  }

  /* ReactMarkdown generated content */
  p {
    font-size: 1.2rem;
    margin: 0;
  }

  strong {
    font-weight: 700;
    color: var(--white);
  }
`;

const LiveRafflesSection = styled.div`
  margin-top: 35px;
  padding-top: 30px;
  border-top: 1px solid #444;
  text-align: center;

  h3 {
    font-weight: 700;
    margin-bottom: 15px;
    text-transform: capitalize;
  }

  p {
    font-size: 1.2rem;
    line-height: 1.6;
    color: #ddd;
  }
`;

const GrandPrizeBoxInline = styled.div`
  background: #333333;
  border: 2px solid #4db84d;
  padding: 20px 25px;
  border-radius: 20px;
  text-align: center;
  font-size: 1.2rem;
  margin-top: 35px;
  line-height: 1.6;

  .grand-prize-title {
    color: var(--white);
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 20px;

    .bonus-text {
      color: #4db84d;
    }
  }

  p {
    margin: 0;
    font-size: 1.2rem;
    line-height: 1.6;

    .green-text {
      color: #4db84d;
      font-weight: 400;
    }

    .white-italic {
      color: var(--white);
      font-style: italic;
      font-weight: 400;
    }
  }

  strong {
    font-weight: 700;
    color: var(--white);
    font-style: italic;
  }

  @media screen and (max-width: 768px) {
    font-size: 1.2rem;
    padding: 18px 20px;

    .grand-prize-title {
      font-size: 24px;
      margin-bottom: 15px;
    }

    p {
      font-size: 1.2rem;
    }
  }
`;

const Bet99RaffleSection = styled.div`
  background: var(--black);
  padding: 40px 20px;
  text-align: center;

  .raffle-container {
    background: #333333;
    border: 2px solid #4db84d;
    border-radius: 20px;
    padding: 50px 40px;
    max-width: 1200px;
    margin: 0 auto;
  }

  h2 {
    font-size: 36px;
    font-weight: 900;
    margin-bottom: 20px;
    text-transform: none;
    color: var(--white);
    letter-spacing: 1px;
    strong {
      color: #4db84d;
      font-weight: 900;
    }

    .green-text {
      color: #4db84d;
    }
  }

  .raffle-intro {
    font-size: 1.2rem !important;
    line-height: 1.6;
    margin-bottom: 30px;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    color: var(--white);

    p {
      margin-bottom: 0;
    }

    strong {
      font-weight: 700;
    }
  }

  .raffle-prizes {
    margin: 30px 0;

    .raffle-prize {
      padding: 10px;
      display: flex;

      .prize-box {
        background: #2ecc71;
        color: var(--white);
        padding: 20px;
        border-radius: 20px;
        font-size: 1.2rem;
        font-weight: 600;
        min-height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        line-height: 1.3;
        width: 100%;
      }
    }
  }

  .raffle-disclaimer {
    font-size: 1.2rem;
    color: var(--white);
    margin-top: 20px;
    font-style: italic;
  }

  @media screen and (max-width: 768px) {
    padding: 40px 20px 20px;

    .raffle-container {
      padding: 30px 20px;
    }

    h2 {
      font-size: 28px;
    }

    .raffle-intro {
      font-size: 1.2rem;
    }

    .raffle-prizes .raffle-prize .prize-box {
      font-size: 1.2rem;
      padding: 25px 15px;
      min-height: 90px;
    }
  }
`;

const Bet99SignupBox = styled.div`
  background: transparent;
  border: none;
  color: var(--white);
  padding: 10px;
  text-align: center;
  margin: 0 auto;
  max-width: 900px;

  p {
    line-height: 1.6;
    margin-bottom: 0;
  }

  strong {
    font-weight: 700;
    color: var(--white);
  }

  a {
    color: #4db84d;
    text-decoration: underline;
    font-weight: 700;

    &:hover {
      text-decoration: underline;
      color: #3da73d;
    }
  }

  @media screen and (max-width: 768px) {
    padding: 10px;
  }
`;

const WatchSection = styled.div`
  background: var(--black);
  padding: 60px 40px;
  text-align: center;

  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const EventDetailsSection = styled.div`
  background: var(--lightblack);
  padding: 20px;

  .event-image {
    padding: 20px;

    img {
      border-radius: 8px;
      width: 100%;
      height: auto;
    }
  }

  .event-details-content {
    padding: 20px 20px 20px 40px;
    display: block;
    text-align: left;

    h2 {
      font-size: 36px;
      font-weight: 700;
      margin-bottom: 25px;
      color: var(--white);
    }

    ul {
      padding: 0;
      margin: 0;
      list-style: disc;
      padding-left: 20px;

      li {
        font-size: 1.2rem;
        margin-bottom: 15px;
        color: var(--white);
        line-height: 1.5;

        p {
          display: inline;
          margin: 0;
          font-size: 1.2rem;

          strong {
            font-weight: 700;
            color: var(--white);
          }
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    padding: 40px 20px;

    .event-details-content {
      padding: 20px;

      h2 {
        font-size: 28px;
        margin-bottom: 20px;
      }

      ul li {
        font-size: 1.2rem;

        p {
          font-size: 1.2rem;
        }
      }
    }
  }
`;

const FinalTagline = styled.div`
  background: var(--black);
  padding: 60px 40px;
  text-align: center;

  h2 {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 15px;
    text-transform: capitalize;
  }

  p {
    font-size: 1.2rem;
    margin: 0;
  }

  @media screen and (max-width: 768px) {
    padding: 40px 20px;

    h2 {
      font-size: 28px;
    }

  }
`;

export default BigGameBash2026;
